function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Log from '@deephaven/log';
import { usePrevious, useThrottledCallback } from '@deephaven/react-hooks';
import { ErrorBoundary } from '@deephaven/components';
import { useDispatch, useSelector } from 'react-redux';
import PanelManager from "./PanelManager.js";
import PanelErrorBoundary from "./PanelErrorBoundary.js";
import LayoutUtils, { isReactComponentConfig } from "./layout/LayoutUtils.js";
import { canHaveRef, dehydrate as dehydrateDefault, hydrate as hydrateDefault } from "./DashboardUtils.js";
import PanelEvent from "./PanelEvent.js";
import { useListener } from "./layout/index.js";
import { getDashboardData, updateDashboardData } from "./redux/index.js";
import DashboardPanelWrapper from "./DashboardPanelWrapper.js";
import { PanelIdContext } from "./usePanelId.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('DashboardLayout');
var EMPTY_OBJECT = Object.freeze({});
var DEFAULT_LAYOUT_CONFIG = [];
var DEFAULT_CALLBACK = () => undefined;
var STATE_CHANGE_THROTTLE_MS = 1000;

// If a component isn't registered, just pass through the props so they are saved if a plugin is loaded later
var FALLBACK_CALLBACK = props => props;
/**
 * DashboardLayout component. Handles hydrating, dehydrating components, listening for dragging panels.
 */
export function DashboardLayout(_ref) {
  var _useSelector, _closed;
  var id = _ref.id,
    children = _ref.children,
    _ref$emptyDashboard = _ref.emptyDashboard,
    emptyDashboard = _ref$emptyDashboard === void 0 ? /*#__PURE__*/_jsx("div", {
      className: "dashboard-empty",
      children: "Dashboard is empty."
    }) : _ref$emptyDashboard,
    layout = _ref.layout,
    _ref$layoutConfig = _ref.layoutConfig,
    layoutConfig = _ref$layoutConfig === void 0 ? DEFAULT_LAYOUT_CONFIG : _ref$layoutConfig,
    _ref$onLayoutChange = _ref.onLayoutChange,
    onLayoutChange = _ref$onLayoutChange === void 0 ? DEFAULT_CALLBACK : _ref$onLayoutChange,
    _ref$onLayoutInitiali = _ref.onLayoutInitialized,
    onLayoutInitialized = _ref$onLayoutInitiali === void 0 ? DEFAULT_CALLBACK : _ref$onLayoutInitiali,
    _ref$hydrate = _ref.hydrate,
    hydrate = _ref$hydrate === void 0 ? hydrateDefault : _ref$hydrate,
    _ref$dehydrate = _ref.dehydrate,
    dehydrate = _ref$dehydrate === void 0 ? dehydrateDefault : _ref$dehydrate,
    panelWrapper = _ref.panelWrapper;
  var dispatch = useDispatch();
  var data = (_useSelector = useSelector(state => getDashboardData(state, id))) !== null && _useSelector !== void 0 ? _useSelector : EMPTY_OBJECT;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDashboardEmpty = _useState2[0],
    setIsDashboardEmpty = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isItemDragging = _useState4[0],
    setIsItemDragging = _useState4[1];
  // `lastConfig` is intentionally a ref rather than state so it can be written synchronously inside the throttled body before `onLayoutChange` runs.
  // This guarantees any subsequent render triggered by the parent observes the latest value, regardless of how that render was scheduled (see DH-21843).
  var lastConfigRef = useRef();
  var _useState5 = useState((_closed = data === null || data === void 0 ? void 0 : data.closed) !== null && _closed !== void 0 ? _closed : []),
    _useState6 = _slicedToArray(_useState5, 1),
    initialClosedPanels = _useState6[0];
  var _useState7 = useState(layout.getReactChildren()),
    _useState8 = _slicedToArray(_useState7, 2),
    layoutChildren = _useState8[0],
    setLayoutChildren = _useState8[1];
  var hydrateMap = useMemo(() => new Map(), []);
  var dehydrateMap = useMemo(() => new Map(), []);
  var registerComponent = useCallback(function (name, componentType) {
    var _componentType$displa;
    var componentHydrate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : hydrate;
    var componentDehydrate = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : dehydrate;
    log.debug2('registerComponent', name, componentType, componentHydrate, componentDehydrate);
    function wrappedComponent(props, ref) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      var CType = componentType;
      var PanelWrapperType = panelWrapper;

      /**
       * The ref is used to detect changes to class component state so we
       * can track changes to panelState. We should opt for more explicit
       * state changes in the future and in functional components.
       */
      var hasRef = canHaveRef(CType);
      var innerElem = hasRef ?
      /*#__PURE__*/
      // eslint-disable-next-line react/jsx-props-no-spreading
      _jsx(CType, _objectSpread(_objectSpread({}, props), {}, {
        ref: ref
      })) :
      /*#__PURE__*/
      // eslint-disable-next-line react/jsx-props-no-spreading
      _jsx(CType, _objectSpread({}, props));

      // Props supplied by GoldenLayout
      var glContainer = props.glContainer,
        glEventHub = props.glEventHub;
      var panelId = LayoutUtils.getIdFromContainer(glContainer);
      return /*#__PURE__*/_jsx(PanelErrorBoundary, {
        glContainer: glContainer,
        glEventHub: glEventHub,
        children: /*#__PURE__*/_jsx(PanelIdContext.Provider, {
          value: panelId,
          children: /*#__PURE__*/_jsx(DashboardPanelWrapper, _objectSpread(_objectSpread({}, props), {}, {
            children: PanelWrapperType == null ? innerElem :
            /*#__PURE__*/
            // eslint-disable-next-line react/jsx-props-no-spreading
            _jsx(PanelWrapperType, _objectSpread(_objectSpread({}, props), {}, {
              children: innerElem
            }))
          }))
        })
      });
    }
    wrappedComponent.displayName = "DashboardWrapper(".concat((_componentType$displa = componentType.displayName) !== null && _componentType$displa !== void 0 ? _componentType$displa : name, ")");
    var cleanup = layout.registerComponent(name, /*#__PURE__*/React.forwardRef(wrappedComponent));
    hydrateMap.set(name, componentHydrate);
    dehydrateMap.set(name, componentDehydrate);
    return cleanup;
  }, [hydrate, dehydrate, hydrateMap, dehydrateMap, layout, panelWrapper]);
  var hydrateComponent = useCallback((name, props) => {
    var _hydrateMap$get;
    return ((_hydrateMap$get = hydrateMap.get(name)) !== null && _hydrateMap$get !== void 0 ? _hydrateMap$get : FALLBACK_CALLBACK)(props, id);
  }, [hydrateMap, id]);
  var dehydrateComponent = useCallback((name, config) => {
    var _dehydrateMap$get;
    return ((_dehydrateMap$get = dehydrateMap.get(name)) !== null && _dehydrateMap$get !== void 0 ? _dehydrateMap$get : FALLBACK_CALLBACK)(config, id);
  }, [dehydrateMap, id]);
  var panelManager = useMemo(() => new PanelManager(layout, hydrateComponent, dehydrateComponent, new Map(), initialClosedPanels, _ref2 => {
    var closed = _ref2.closed,
      openedMap = _ref2.openedMap;
    dispatch(updateDashboardData(id, {
      closed,
      openedMap
    }));
  }), [dehydrateComponent, dispatch, hydrateComponent, id, initialClosedPanels, layout]);

  // Throttle the calls so that we don't flood comparing these layouts
  var throttledProcessDehydratedLayoutConfig = useThrottledCallback(dehydratedLayoutConfig => {
    var previousConfig = lastConfigRef.current;
    var hasChanged = previousConfig == null || !LayoutUtils.isEqual(previousConfig, dehydratedLayoutConfig);
    log.debug('handleLayoutStateChanged', hasChanged, dehydratedLayoutConfig);
    if (hasChanged) {
      log.debug('Layout config has changed', 'lastConfig', previousConfig, 'dehydratedLayoutConfig', dehydratedLayoutConfig);
      setIsDashboardEmpty(layout.root.contentItems.length === 0);

      // Update the ref synchronously before calling `onLayoutChange` so
      // that any subsequent render triggered by the parent observes the
      // latest value, regardless of how that render was scheduled.
      lastConfigRef.current = dehydratedLayoutConfig;
      onLayoutChange(dehydratedLayoutConfig);
      setLayoutChildren(layout.getReactChildren());
    }
  }, STATE_CHANGE_THROTTLE_MS, {
    flushOnUnmount: true
  });
  useEffect(() => () => throttledProcessDehydratedLayoutConfig.flush(), [throttledProcessDehydratedLayoutConfig]);
  var handleLayoutStateChanged = useCallback(() => {
    // we don't want to emit stateChanges that happen during item drags or else
    // we risk the last saved state being one without that panel in the layout entirely
    if (isItemDragging) return;
    var glConfig = layout.toConfig();
    var contentConfig = glConfig.content;
    var dehydratedLayoutConfig = LayoutUtils.dehydrateLayoutConfig(contentConfig, dehydrateComponent);
    throttledProcessDehydratedLayoutConfig(dehydratedLayoutConfig);
  }, [dehydrateComponent, isItemDragging, layout, throttledProcessDehydratedLayoutConfig]);
  var handleLayoutItemPickedUp = useCallback(component => {
    var componentId = LayoutUtils.getIdFromContainer(component);
    layout.eventHub.emit(PanelEvent.DRAGGING, componentId);
    setIsItemDragging(true);
  }, [layout.eventHub]);
  var handleLayoutItemDropped = useCallback(component => {
    var componentId = LayoutUtils.getIdFromContainer(component);
    layout.eventHub.emit(PanelEvent.DROPPED, componentId);
    setIsItemDragging(false);
  }, [layout.eventHub]);
  var handleComponentCreated = useCallback(item => {
    log.debug2('handleComponentCreated', item);
    if (item == null || item.config == null || !isReactComponentConfig(item.config) || item.config.component == null || item.element == null) {
      return;
    }
    var cssComponent = item.config.component.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    var cssClass = "".concat(cssComponent, "-component");
    item.element.addClass(cssClass);
  }, []);
  var handleReactChildrenChange = useCallback(() => {
    setLayoutChildren(layout.getReactChildren());
  }, [layout]);
  useListener(layout, 'stateChanged', handleLayoutStateChanged);
  useListener(layout, 'itemPickedUp', handleLayoutItemPickedUp);
  useListener(layout, 'itemDropped', handleLayoutItemDropped);
  useListener(layout, 'componentCreated', handleComponentCreated);
  useListener(layout.eventHub, PanelEvent.TITLE_CHANGED, handleLayoutStateChanged);
  useListener(layout, 'reactChildrenChanged', handleReactChildrenChange);
  var previousLayoutConfig = usePrevious(layoutConfig);
  useEffect(function loadNewConfig() {
    if (previousLayoutConfig !== layoutConfig && layoutConfig !== lastConfigRef.current) {
      log.debug('loadNewConfig effect triggered. previousLayoutConfig', previousLayoutConfig, 'layoutConfig', layoutConfig, 'lastConfig', lastConfigRef.current);
      log.debug('Setting new layout content...');
      var content = LayoutUtils.hydrateLayoutConfig(layoutConfig, hydrateComponent);
      // Remove the old layout before add the new one
      while (layout.root.contentItems.length > 0) {
        layout.root.contentItems[0].remove();
      }

      // Add the new content. It is usally just one item from the root
      for (var i = 0; i < content.length; i += 1) {
        layout.root.addChild(content[i]);
      }
      setIsDashboardEmpty(layout.root.contentItems.length === 0);
    }
  }, [hydrateComponent, layout, layoutConfig, panelManager, previousLayoutConfig]);

  // This should be the last hook called in this component
  // Ensures it runs after any other effects on mount
  // Fire only once after the layout is mounted
  // This should ensure DashboardPlugins have been mounted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onLayoutInitialized(), []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isDashboardEmpty && emptyDashboard, layoutChildren, React.Children.map(children, child => child != null ?
    /*#__PURE__*/
    // Have fallback be an empty array so that we don't show the error message over entire app
    // Look into using toast message in the future
    _jsx(ErrorBoundary, {
      fallback: [],
      children: /*#__PURE__*/React.cloneElement(child, {
        id,
        layout,
        panelManager,
        registerComponent
      })
    }) : null)]
  });
}
export default DashboardLayout;
//# sourceMappingURL=DashboardLayout.js.map